"use client";
import { type Dispatch, memo, useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";

import {
	Hits,
	useHits,
	useInstantSearch,
	useSearchBox,
	useStats,
} from "react-instantsearch";
import type { Hit, BaseHit } from "instantsearch.js";

import { t } from "@/utils";
import FilterKeywordSearch from "../FilterKeywordSearch/FilterKeywordSearch";
import { HmcSpinner } from "@adi-ctx/harmonic-core-components-react";
import fromSearchDto from "@/lib/api/mappers/from-search-dto";
import { Link, SearchResultCard, type TFilterCategory } from "@/components";
import { hitsPerPage } from "@/lib/constants/search.constants";

export type SearchBoxProps = {
	readonly setItemsCb: Dispatch<Hit[]>;
	readonly filter: TFilterCategory | undefined;
};

function SearchBox({ setItemsCb, filter }: SearchBoxProps) {
	const [isLoading, setIsLoading] = useState(false);

	const searchParams = useSearchParams();

	const { items } = useHits();
	const { status } = useInstantSearch();
	const { refine } = useSearchBox();
	const { nbHits } = useStats();

	const hitsLimit = nbHits > hitsPerPage ? hitsPerPage : nbHits;
	const filteredHits = filter
		? items.filter((item) => item.type === filter?.id).length
		: hitsLimit;
	const numberOfHits = searchParams.get("keyword") ? filteredHits : 0;
	const label = filter?.label
		? `in ${filter.label}`
		: t("filters.results-all-category");
	const numberOfHitsMsg =
		numberOfHits === 1 ? t("filters.result") : t("filters.results");

	const handleOnChange = (newQuery: string) => {
		const params = new URLSearchParams();
		params.set("keyword", newQuery);
		const updatedPath = `?${params.toString()}`;
		window.history.pushState(null, "", updatedPath);

		refine(newQuery);
	};

	// eslint-disable-next-line react/function-component-definition
	const Hit = ({ hit }: { readonly hit: Hit<BaseHit> }) => {
		const card = fromSearchDto(hit, filter);

		return (
			<Link href={card?.link?.url} rel="noopener noreferrer" target="_blank">
				<SearchResultCard card={card} hit={hit} />
			</Link>
		);
	};

	useEffect(() => {
		if (searchParams.get("keyword")) {
			refine(searchParams.get("keyword") ?? "");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (status === "idle") {
			setIsLoading(false);
		} else if (status === "loading") {
			setIsLoading(true);
		}
	}, [status]);

	useEffect(() => {
		if (items.length > 0) {
			setItemsCb(items);
		} else {
			setItemsCb([]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [items]);

	return (
		<>
			<div className="h-[40px] w-full">
				<FilterKeywordSearch
					value={searchParams.get("keyword") ?? ""}
					placeholder={t("search.button-search-title")}
					onChange={handleOnChange}
				/>
			</div>

			{isLoading ? (
				<div className="flex h-full w-full justify-center p-4 sm:py-10">
					<div className="flex flex-col items-center gap-4">
						<HmcSpinner kind="secondary" size="md" />
						{t("auth.action-in-progress")}
					</div>
				</div>
			) : (
				<>
					<p className="hmc-title-xs mb-4 mt-6" data-testid="filter-results">
						{numberOfHits} {numberOfHitsMsg}
						<span className="text-contentSubtle">
							&nbsp;
							{label}
						</span>
					</p>

					{searchParams.get("keyword") && <Hits hitComponent={Hit} />}
				</>
			)}
		</>
	);
}

export default memo(SearchBox);
